import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { readArtifactType, readRememberedArtifactType, rememberArtifactType } from '../../lib/artifactPreferences';
import { SCRIPT_KINDS, SCRIPT_KIND_DESCRIPTIONS, CALLSHEET_DOC_TYPES, CALLSHEET_DOC_TYPE_DESCRIPTIONS, type ScriptKind, type CallsheetDocType } from '../../../shared/artifactTypes';
import { ARTIFACT_TOOL_LABELS, type ProjectArtifactRow } from '../../../shared/types';
import { DateField, Modal } from '../../components/primitives';
import { ReviewArtifactActions } from '../../components/ReviewArtifactActions';

/** "New script" / "New call sheet" / "New review" create-in-context: title +
    (call sheets only) shoot date, POSTs to the create route, then hands the
    fresh project_artifacts row back so the caller can navigate straight into
    the embedded editor. */
export function NewArtifactModal({
  tool,
  projectId,
  seedTitle,
  seedShootDate,
  onClose,
  onCreated,
  onRetained,
  dashboardSlug,
}: {
  tool: 'script' | 'callsheet' | 'review';
  projectId: string;
  seedTitle: string;
  seedShootDate: string | null;
  onClose: () => void;
  onCreated: (artifact: ProjectArtifactRow) => void;
  /** Record partial creation without closing the warning or opening the editor. */
  onRetained?: (artifact: ProjectArtifactRow) => void;
  dashboardSlug?: string | null;
}) {
  const label = ARTIFACT_TOOL_LABELS[tool];
  const [title, setTitle] = useState(seedTitle);
  const [shootDate, setShootDate] = useState<string | null>(seedShootDate);
  const [lastUsedType] = useState(() => readRememberedArtifactType(tool));
  const [artifactType, setArtifactType] = useState(() => readArtifactType(tool));
  // Unchanged retries share a key; editing or reopening starts a new sheet.
  const [idempotencyKey, setIdempotencyKey] = useState(() => tool === 'callsheet' ? crypto.randomUUID() : undefined);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [retained, setRetained] = useState<ProjectArtifactRow | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [sharingActive, setSharingActive] = useState(false);

  const submit = async () => {
    if (!title.trim() || busyRef.current || retained) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ artifact: ProjectArtifactRow; warning?: string }>(`/api/admin/projects/${projectId}/artifacts/create`, {
        method: 'POST',
        json: {
          tool,
          title: title.trim(),
          kind: tool === 'script' ? artifactType : undefined,
          doc_type: tool === 'callsheet' ? artifactType : undefined,
          shoot_date: tool === 'callsheet' ? shootDate : undefined,
          idempotency_key: idempotencyKey,
        },
      });
      if (tool !== 'review') rememberArtifactType(tool, artifactType);
      if (tool === 'review' && res.warning) {
        setRetained(res.artifact);
        setWarning(res.warning);
        onRetained?.(res.artifact);
        return;
      }
      onCreated(res.artifact);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to create ${label.toLowerCase()}.`);
    } finally { busyRef.current = false; setBusy(false); }
  };

  return (
    <Modal
      title={retained ? 'Review created' : `New ${label.toLowerCase()}`}
      context="TOOLS"
      mobile="half"
      width={tool === 'review' ? undefined : 620}
      onClose={() => { if (!busyRef.current && !sharingActive) onClose(); }}
      hint={retained ? 'Saved to this project. Finish sharing below.' : 'Opens embedded once created'}
      foot={
        <>
          <button className="btn" onClick={onClose} disabled={busy || sharingActive}>
            {retained ? 'Done' : 'Cancel'}
          </button>
          {retained ? <button className="btn primary" disabled={!retained.admin_url || sharingActive} onClick={() => onCreated(retained)}>Open review ↗</button> :
            <button className="btn primary" onClick={submit} disabled={!title.trim() || busy}>Create {label.toLowerCase()} →</button>}
        </>
      }
    >
      {retained ? <div>
        <p>{warning ? 'Your review is saved. Sharing still needs attention.' : 'Your review is saved. Copy its client link or open the editor.'}</p>
        {warning && <p className="error-text" role="alert" style={{ marginTop: 12 }}>{warning}</p>}
        <ReviewArtifactActions artifact={retained} dashboardSlug={dashboardSlug} onInteractionChange={setSharingActive} onUpdated={(artifact) => {
          setRetained(artifact); setWarning(null); onRetained?.(artifact);
        }} />
      </div> : <div className="form-grid">
        <div className="f-field span-2">
          <span className="f-label">
            Title <span className="req">*</span>
          </span>
          <input className="f-input" value={title} onChange={(e) => {
            setTitle(e.target.value);
            if (tool === 'callsheet') setIdempotencyKey(crypto.randomUUID());
          }} autoFocus />
        </div>
        {tool !== 'review' && (
          <div className="f-field span-2">
            <div className="f-label-row">
              <span className="f-label" id="artifact-kind-label">{tool === 'script' ? 'Template' : 'Type'}</span>
              <span className="f-help" id="artifact-kind-hint">
                {tool === 'script' || lastUsedType ? 'Remembered for next time' : 'Default: Call sheet'}
              </span>
            </div>
            <div className={`kind-grid${tool === 'callsheet' ? ' two' : ''}`} role="radiogroup"
              aria-labelledby="artifact-kind-label" aria-describedby="artifact-kind-hint">
              {Object.entries(tool === 'script' ? SCRIPT_KINDS : CALLSHEET_DOC_TYPES).map(([value, optionLabel]) => (
                <label key={value} className={`kind-card${artifactType === value ? ' on' : ''}`}>
                  {value === lastUsedType && <span className="kind-tag">Last used</span>}
                  <span className="kind-name">
                    <input type="radio" className="radio" name="artifact-kind" value={value}
                      checked={artifactType === value} disabled={busy} aria-label={optionLabel}
                      aria-describedby={`artifact-kind-${value}-desc`}
                      onChange={() => {
                        setArtifactType(value);
                        if (tool === 'callsheet') setIdempotencyKey(crypto.randomUUID());
                      }} />
                    <span>{optionLabel}</span>
                  </span>
                  <span className="kind-desc" id={`artifact-kind-${value}-desc`}>
                    {tool === 'script' ? SCRIPT_KIND_DESCRIPTIONS[value as ScriptKind] : CALLSHEET_DOC_TYPE_DESCRIPTIONS[value as CallsheetDocType]}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
        {tool === 'callsheet' && (
          <div className="f-field">
            <span className="f-label">Shoot date</span>
            <DateField value={shootDate} onChange={(value) => {
              setShootDate(value);
              setIdempotencyKey(crypto.randomUUID());
            }} />
          </div>
        )}
        {error && <p className="error-text span-2">{error}</p>}
      </div>}
    </Modal>
  );
}
